/* eslint import/no-unresolved: 0 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Alert, breakpoints, Hyperlink, useWindowSize,
} from '@openedx/paragon';
import { Locked } from '@openedx/paragon/icons';
import messages from '@src/courseware/course/sequence/lock-paywall/messages';
import certificateLocked from '@src/generic/assets/edX_locked_certificate.png';
import { useModel } from '@src/generic/model-store';
import { UpgradeButton } from '@src/generic/upgrade-button';
import { getAuthenticatedUser } from '@edx/frontend-platform/auth';
import { sendTrackEvent } from '@edx/frontend-platform/analytics';

import {
  VerifiedCertBullet,
  UnlockGradedBullet,
  FullAccessBullet,
  SupportMissionBullet,
} from '../generic/upsell-bullets';
import { useCohesion } from '../cohesion';

const LockPaywall = ({
  courseId,
}) => {
  const intl = useIntl();
  const course = useModel('coursewareMeta', courseId);
  const {
    accessExpiration,
    marketingUrl,
    offer,
  } = course;

  const {
    org, verifiedMode,
  } = useModel('courseHomeMeta', courseId);

  const { conversion } = useCohesion(courseId);

  // the following variables are set and used for responsive layout to work with
  // whether the NotificationTray is open or not and if there's an offer with longer text
  const shouldDisplayGatedContentTwoColumnsHalf = useWindowSize().width <= breakpoints.large.minWidth;
  const shouldWrapTextOnButton = useWindowSize().width > breakpoints.extraSmall.minWidth;

  const accessExpirationDate = accessExpiration ? new Date(accessExpiration.expirationDate) : null;
  const pastExpirationDeadline = accessExpiration ? new Date(Date.now()) > accessExpirationDate : false;

  if (!verifiedMode) {
    return null;
  }

  const eventProperties = {
    org_key: org,
    courserun_key: courseId,
  };

  const { userId } = getAuthenticatedUser();

  const handleUpgradeClick = () => {
    sendTrackEvent('edx.bi.ecommerce.upsell_links_clicked', {
      ...eventProperties,
      linkCategory: '(none)',
      linkName: 'in_course_upgrade',
      linkType: 'link',
      pageName: 'in_course',
    });

    conversion('Upgraded');
  };

  const handleUpgradeClickPastExpiration = () => {
    sendTrackEvent('edx.bi.ecommerce.gated_content.past_expiration.link_clicked', {
      ...eventProperties,
      linkCategory: 'gated_content',
      linkName: 'course_details',
      linkType: 'link',
      pageName: 'in_course',
    });
  };

  return (
    <Alert variant="light" aria-live="off" icon={Locked} className="lock-paywall-container" data-testid="lock-paywall-lock-paywall-test-id">
      <div className="row">
        <div className="col">
          <h4 aria-level="3">
            <span>{intl.formatMessage(messages['learn.lockPaywall.title'])}</span>
          </h4>
          {pastExpirationDeadline ? (
            <div className="mb-2 upgrade-intro" data-testid="lock-paywall-past-expiration">
              {intl.formatMessage(messages['learn.lockPaywall.content.pastExpiration'])}
              <Hyperlink
                destination={marketingUrl}
                onClick={handleUpgradeClickPastExpiration(eventProperties)}
                target="_blank"
                data-testid="lock-paywall-past-expiration-link"
              >
                {intl.formatMessage(messages['learn.lockPaywall.courseDetails'])}
                <span>
                  {intl.formatMessage({
                    id: 'learn.lockPaywall.content.link.new-tab.screenreader-only.message',
                    defaultMessage: 'in a new tab',
                    description: 'Screenreader only text for new tab link',
                  })}
                </span>
              </Hyperlink>
            </div>
          ) : (
            <div className="mb-2 upgrade-intro">
              {intl.formatMessage(messages['learn.lockPaywall.content'])}
            </div>
          )}

          <div className={classNames('d-inline-flex flex-row', { 'flex-wrap': shouldDisplayGatedContentTwoColumnsHalf })}>
            <div style={{ float: 'left' }} className="mr-3 mb-2">
              <img
                alt={intl.formatMessage(messages['learn.lockPaywall.example.alt'])}
                src={certificateLocked}
                className="border-0 certificate-image-banner"
                style={{ height: '128px', width: '175px' }}
              />
            </div>

            <div className="mw-xs list-div">
              <div className="mb-2">
                {intl.formatMessage(messages['learn.lockPaywall.list.intro'])}
              </div>
              <ul className="fa-ul ml-4 pl-2">
                <VerifiedCertBullet />
                <UnlockGradedBullet />
                <FullAccessBullet />
                <SupportMissionBullet />
              </ul>
            </div>
          </div>
        </div>

        {pastExpirationDeadline
          ? null
          : (
            <div
              className={
                classNames('d-flex flex-column align-items-md-center text-right justify-content-center', {
                  'col-md-4 mx-md-3': !shouldDisplayGatedContentTwoColumnsHalf, 'col-md-6': shouldDisplayGatedContentTwoColumnsHalf,
                })
              }
            >
              <UpgradeButton
                offer={offer}
                onClick={() => handleUpgradeClick(eventProperties, userId)}
                verifiedMode={verifiedMode}
                style={{ whiteSpace: shouldWrapTextOnButton ? 'nowrap' : null }}
                data-testid="upgrade-button"
              />
            </div>
          )}
      </div>
    </Alert>
  );
};
LockPaywall.propTypes = {
  courseId: PropTypes.string.isRequired,
};

export default LockPaywall;
